import React, { useEffect, useState } from 'react';
import { Modal } from 'react-bootstrap';

import ValidationAlert from '../../../app/components/ValidationAlert';
import {
  CreateUpdateBankAccountDto,
  GetClientDto,
} from '../../../common/pokCore/autogenerated/pokApiClient';
import { useNotifications } from '../../../common/hooks/useNotifications';
import LastEditorInfo from '../../../app/components/LastEditorInfo';
import { usePokCore } from '../../../common/hooks/usePokCore';
import Button from '../../../common/components/Button/Button';
import { useTranslation } from '../../../common/hooks/useTranslation';

import { BankAccountEditorForm } from './BankAccountEditorForm';

interface BankAccountEditorProps {
  id?: string;
  bankAccount: CreateUpdateBankAccountDto;
  handleClose?: () => void;
  show?: boolean;
  client?: GetClientDto;
  onCompleted: (
    bankAccount: CreateUpdateBankAccountDto,
    id?: string,
  ) => Promise<{
    saved: boolean;
    errors: string[];
  }>;
}

export const BankAccountEditor: React.FC<BankAccountEditorProps> = props => {
  const [bankAccount, setBankAccount] = useState(props.bankAccount);
  const [showSaveErrors, setShowSaveErrors] = useState(false);
  const [saveErrors, setSaveErrors] = useState<string[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  const pok = usePokCore();
  const notifications = useNotifications();
  const { t, tk } = useTranslation('administrationLng');

  useEffect(() => {
    if (props.show) {
      setSaveErrors([]);
      setShowSaveErrors(false);
      setBankAccount(props.bankAccount);
    }
  }, [props.bankAccount, props.show]);

  const createBankAccount = async () => {
    setIsLoading(true);
    props
      .onCompleted(bankAccount, props.id)
      .then(saveStatus => {
        if (!saveStatus.saved) {
          setSaveErrors(saveStatus.errors);
          setShowSaveErrors(true);
          return;
        }
        notifications.saveCompleted();
      })
      .catch(async errorResponse => {
        notifications.caughtError(errorResponse);
      })
      .finally(() => setIsLoading(false));
  };

  const propertyChange = (obj: Partial<CreateUpdateBankAccountDto>) => {
    setBankAccount({ ...bankAccount, ...obj });
  };

  return (
    <>
      <Modal
        onHide={props.handleClose}
        show={props.show}
        centered={true}
        keyboard={true}
        backdrop="static"
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {props.id
              ? t(tk.administration.bankAccountEdit)
              : t(tk.administration.bankAccountAdd)}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <BankAccountEditorForm
            id={props.id}
            bankAccount={bankAccount}
            client={props.client}
            propertyChange={propertyChange}
          />
          <ValidationAlert
            show={showSaveErrors}
            errors={saveErrors}
            className="m-3"
          />
        </Modal.Body>
        <Modal.Footer>
          {props.id && (
            <LastEditorInfo
              id={props.id}
              method={pok.bankAccounts.getLastEditor}
            />
          )}
          <Button
            variant="outline-secondary"
            onClick={props.handleClose}
            disabled={isLoading}
          >
            {t(tk.button.close)}
          </Button>
          <Button
            variant="primary"
            onClick={createBankAccount}
            isLoading={isLoading}
          >
            {t(tk.button.save)}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};
